/**
 * Inactivity-based auto-logout policy. Tracks a single "last activity"
 * timestamp fed by both local DOM events and cross-tab `BroadcastChannel`
 * messages, then drives a countdown state machine off a 1-second timer.
 *
 * State transitions (per L1 / L4 in the plan):
 *
 *   ACTIVE   ──50 min idle──▶  WARNING   ──60 sec passes──▶  LOGGING_OUT
 *      ▲                          │                              │
 *      └──── extendSession ───────┘                              │
 *      ▲                                                         │
 *      └─ local OR peer-tab activity (resets lastActivityAt) ────┘
 *
 * Idle is measured as `now - lastActivityAt`, where `lastActivityAt` is
 * updated by *any* tab's activity. A background tab therefore never warns
 * or logs out while the user is active in another tab — the local-only
 * signal that Mantine's `useIdle` exposes can't express that, which is why
 * this hook tracks the timestamp itself.
 *
 * On entering LOGGING_OUT, calls `onLogout` exactly once and broadcasts
 * `signed-out` so peer tabs tear down too. The host layout wires `onLogout`
 * to `performLogout("inactivity", …)`, which revokes the Medplum session
 * and snapshots dirty forms before unmounting the tree.
 *
 * @agentic connections
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createInactivityBroadcast,
  type InactivityBroadcast,
} from "@/app/auth/inactivityBroadcast";
import { WARNING_AT_MS, LOGOUT_AT_MS } from "@/app/auth/inactivityConfig";

/** Throttle for local-activity side effects (broadcast + lastActivity
 *  write). @agentic connections */
export const REBROADCAST_THROTTLE_MS = 5_000;

/** Phase of the inactivity state machine. @agentic connections */
export type InactivityPhase = "active" | "warning" | "logging-out";

/** Inputs to {@link useInactivityLogout}. @agentic connections */
export type UseInactivityLogoutInputs = {
  /** When false, the hook does not subscribe to idle events. */
  enabled: boolean;
  /** Called exactly once when the countdown reaches zero. */
  onLogout: () => void;
  /**
   * Optional sink for activity-tick observations (used by Phase 5 to
   * persist `lastActivityAt` to localStorage). For high-frequency local
   * events it is throttled to at most once per `REBROADCAST_THROTTLE_MS`;
   * peer-tab activity (already throttled at the source) and explicit
   * session extension always fire it.
   */
  onActivityTick?: (at: number) => void;
  /** Optional broadcast factory; defaults to the BroadcastChannel impl.
   *  Tests inject a mock to assert cross-tab semantics. */
  broadcastFactory?: () => InactivityBroadcast;
  /**
   * Optional reader for the cross-tab shared "last activity" timestamp
   * (persisted to `localStorage` by every tab's `onActivityTick`). Polled
   * each tick as a reliable second cross-tab signal: if a peer tab wrote
   * activity, this tab adopts it and stays signed in even when a
   * `BroadcastChannel` message was missed (background-tab delivery is not
   * guaranteed; `localStorage` is). Same-origin only — across ports it
   * returns nothing, which is correct.
   */
  getSharedLastActivity?: () => number | undefined;
};

/** Outputs from {@link useInactivityLogout}. @agentic connections */
export type UseInactivityLogoutResult = {
  phase: InactivityPhase;
  /** Seconds remaining in the warning countdown. 0 outside warning. */
  secondsUntilLogout: number;
  /** User-initiated dismissal of the warning ("Stay signed in"). */
  extendSession: () => void;
};

/** Default-event activity definition: matches Mantine's `useIdle`
 *  defaults — keydown, mousemove, touchmove, click, scroll. */
const ACTIVITY_EVENTS = [
  "keydown",
  "mousemove",
  "touchmove",
  "click",
  "scroll",
] as const;

export const useInactivityLogout = ({
  enabled,
  onLogout,
  onActivityTick,
  broadcastFactory = createInactivityBroadcast,
  getSharedLastActivity,
}: UseInactivityLogoutInputs): UseInactivityLogoutResult => {
  const broadcast = useMemo(() => broadcastFactory(), [broadcastFactory]);

  const [phase, setPhase] = useState<InactivityPhase>("active");
  const [logoutAtMs, setLogoutAtMs] = useState<number | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());

  // Stable refs so the effects below don't churn deps on every render.
  // Per React Hooks v7's `react-hooks/refs` rule, ref mutations are
  // confined to effects and platform-fired callbacks (events, timers),
  // never the render body.
  const onLogoutRef = useRef(onLogout);
  const onActivityTickRef = useRef(onActivityTick);
  const getSharedLastActivityRef = useRef(getSharedLastActivity);
  // Current phase, mirrored into a ref so the DOM-event handler (registered
  // once) can read it without re-subscribing. Used to ignore passive local
  // activity once the warning is showing.
  const phaseRef = useRef(phase);
  // Single source of truth for "when did anyone last interact". Updated by
  // local DOM events, peer-tab activity broadcasts, and extendSession.
  // Seeded in a mount effect (Date.now() is impure, so it can't run in the
  // render body); the 1-second timer fires well after mount effects, so it
  // never observes the 0 sentinel.
  const lastActivityRef = useRef<number>(0);
  // Throttle window for the local-event side effects.
  const lastTickRef = useRef<number>(0);
  const onLogoutCalledRef = useRef(false);

  useEffect(() => {
    onLogoutRef.current = onLogout;
    onActivityTickRef.current = onActivityTick;
    getSharedLastActivityRef.current = getSharedLastActivity;
  }, [onLogout, onActivityTick, getSharedLastActivity]);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Reset the idle clock and clear any in-flight warning. Pure state +
  // ref updates; callers decide whether to also run the throttled side
  // effects (broadcast + onActivityTick).
  const registerActivity = useCallback((at: number) => {
    lastActivityRef.current = at;
    onLogoutCalledRef.current = false;
    setPhase((prev) => (prev === "active" ? prev : "active"));
    setLogoutAtMs((prev) => (prev === null ? prev : null));
  }, []);

  // Local DOM activity. `registerActivity` runs on every event (cheap ref
  // write + bail-out setState), but the side effects — the cross-tab
  // broadcast and the `onActivityTick` localStorage write — are throttled
  // so high-frequency events (mousemove/scroll) never hammer storage.
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = () => {
      // Once the warning is showing, passive activity (a stray mousemove on
      // the way to the "Stay signed in" button, a scroll) must NOT silently
      // dismiss it — the modal is non-dismissable and only the explicit
      // button (extendSession) or genuine peer-tab activity counts. Without
      // this, moving the cursor toward the button cancels the warning,
      // making the button unreachable.
      if (phaseRef.current !== "active") return;
      const ts = Date.now();
      registerActivity(ts);
      if (ts - lastTickRef.current < REBROADCAST_THROTTLE_MS) return;
      lastTickRef.current = ts;
      onActivityTickRef.current?.(ts);
      broadcast.post({ type: "activity", at: ts });
    };
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, handler, { passive: true });
    }
    return () => {
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, handler);
      }
    };
  }, [enabled, registerActivity, broadcast]);

  // Receive cross-tab broadcast.
  useEffect(() => {
    if (!enabled) return undefined;
    return broadcast.subscribe((message) => {
      if (message.type === "activity") {
        // Peer-tab activity counts as our activity: refresh the shared
        // timestamp so our idle clock matches theirs. The sender already
        // throttles, so no echo and no extra throttle needed here.
        registerActivity(message.at);
        onActivityTickRef.current?.(message.at);
        return;
      }
      if (message.type === "signed-out") {
        // A peer tab's inactivity timer fired and asked everyone to tear
        // down. Run the same logout once so we don't leave a stale
        // session or a dangling warning modal. Don't re-broadcast — the
        // originating tab already notified every peer.
        if (onLogoutCalledRef.current) return;
        onLogoutCalledRef.current = true;
        setPhase("logging-out");
        setLogoutAtMs(null);
        try {
          onLogoutRef.current();
        } catch (error) {
          console.error("[useInactivityLogout] onLogout threw", error);
        }
      }
    });
  }, [enabled, registerActivity, broadcast]);

  // Single 1-second timer evaluates idle from the shared timestamp and
  // drives the state machine. setState inside the interval callback is
  // event-handler-equivalent (timer fired by the platform), not in-effect.
  // While inside the active window the tick performs no setState, so it
  // does not re-render the tree every second.
  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(() => {
      const ts = Date.now();
      // Adopt a peer tab's activity from the shared (localStorage) timestamp
      // if it's newer than ours. This is the reliable cross-tab signal —
      // it keeps this tab alive even when a BroadcastChannel activity
      // message was missed (e.g. background-tab delivery).
      const shared = getSharedLastActivityRef.current?.();
      if (shared !== undefined && shared > lastActivityRef.current) {
        lastActivityRef.current = shared;
      }
      const idleFor = ts - lastActivityRef.current;
      if (idleFor >= LOGOUT_AT_MS) {
        if (onLogoutCalledRef.current) return;
        onLogoutCalledRef.current = true;
        setPhase("logging-out");
        setLogoutAtMs(null);
        // Notify peers so they tear down too.
        broadcast.post({ type: "signed-out", reason: "inactivity" });
        try {
          onLogoutRef.current();
        } catch (error) {
          console.error("[useInactivityLogout] onLogout threw", error);
        }
        return;
      }
      if (idleFor >= WARNING_AT_MS) {
        setPhase((prev) => (prev === "active" ? "warning" : prev));
        setLogoutAtMs((prev) => prev ?? lastActivityRef.current + LOGOUT_AT_MS);
        setNow(ts);
        return;
      }
      // Idle window is fresh (often because a peer tab is active): clear any
      // in-flight warning so a sibling tab's activity dismisses ours.
      onLogoutCalledRef.current = false;
      setPhase((prev) => (prev === "active" ? prev : "active"));
      setLogoutAtMs((prev) => (prev === null ? prev : null));
    }, 1000);
    return () => window.clearInterval(id);
  }, [enabled, broadcast]);

  // Disabled toggled on → drop any in-flight warning. Render-time
  // derivation (the supported "adjust state when a prop changes" pattern)
  // keeps this out of an effect.
  if (!enabled) {
    if (phase !== "active") setPhase("active");
    if (logoutAtMs !== null) setLogoutAtMs(null);
  }

  const extendSession = useCallback(() => {
    const ts = Date.now();
    registerActivity(ts);
    // Explicit "Stay signed in": always notify peers and persist, never
    // throttled.
    lastTickRef.current = ts;
    onActivityTickRef.current?.(ts);
    broadcast.post({ type: "activity", at: ts });
  }, [registerActivity, broadcast]);

  // Tear down the broadcast channel on unmount.
  useEffect(() => () => broadcast.close(), [broadcast]);

  const secondsUntilLogout =
    phase === "warning" && logoutAtMs !== null
      ? Math.max(0, Math.ceil((logoutAtMs - now) / 1000))
      : 0;

  return { phase, secondsUntilLogout, extendSession };
};
